import * as React from 'react';
import type { BatchReserveResponse } from '@ooh/contracts';
import { addCalendarDays } from '@ooh/contracts';
import { toDateStr } from '@ooh/ui/lib/date-string';
import {
  useBatchReservePreviewTtl,
  type useBatchReserve,
} from '../../reservations/api/batch-mutations';
import type { UnifiedSelectCellResolution } from '../lib/unified-select-types';
import { normalizeBatchReserveResponse, toBatchCells } from './selection-helpers';

interface UseUnifiedSelectReserveParams {
  canSubmitReserve: boolean;
  effectiveOfferId: number | null;
  freeCells: UnifiedSelectCellResolution[];
  allResolvedCells: UnifiedSelectCellResolution[];
  batchReserve: ReturnType<typeof useBatchReserve>;
  onBatchReserveSuccess: ((response: BatchReserveResponse) => void) | undefined;
}

type DateRange = { from: Date; to: Date };

type ReserveStatus = 'SOFT_RESERVATION' | 'OPTION';

// Both statuses send every resolved cell (free, held by another party, and the caller's own
// holds) and let the server classify the outcome per cell. Dropping any of them client-side
// would hide a conflict from the confirmation step.
function useReserveSubmit({
  canSubmitReserve,
  effectiveOfferId,
  allResolvedCells,
  batchReserve,
  onBatchReserveSuccess,
}: Omit<UseUnifiedSelectReserveParams, 'freeCells'>) {
  // Shown beside the recomputed selection until dismissed or the reserve dialog opens again.
  const [reserveResult, setReserveResult] = React.useState<BatchReserveResponse | null>(null);

  function submitReserve(
    status: ReserveStatus,
    targetVisibilityDays: number | undefined,
    ttlExpiresAt: Date,
    dateRangeOverride?: DateRange,
  ) {
    if (!canSubmitReserve || effectiveOfferId === null) return;
    batchReserve.mutate(
      {
        offerId: effectiveOfferId,
        status,
        ...(status === 'OPTION' ? { targetVisibilityDays } : {}),
        // `ttlExpiresAt` is required on the wire even for OPTION and travels as a date-only
        // string (a calendar date means end of day, Warsaw).
        ttlExpiresAt: toDateStr(ttlExpiresAt),
        cells: toBatchCells(allResolvedCells, dateRangeOverride),
      },
      {
        onSuccess: (data) => {
          const normalized = normalizeBatchReserveResponse(data);
          setReserveResult(normalized);
          onBatchReserveSuccess?.(normalized);
        },
      },
    );
  }

  return { reserveResult, submitReserve, clearReserveResult: () => setReserveResult(null) };
}

/**
 * Rezerwacja / Opcja flow of the unified SELECT popover: both confirmation dialogs, the TTL
 * preview behind the Rezerwacja dialog, and the last batch outcome.
 */
export function useUnifiedSelectReserve(params: UseUnifiedSelectReserveParams) {
  const { canSubmitReserve, effectiveOfferId, freeCells } = params;
  const previewTtl = useBatchReservePreviewTtl();
  const [optionDialogOpen, setOptionDialogOpen] = React.useState(false);
  const [reserveDialogOpen, setReserveDialogOpen] = React.useState(false);
  const { reserveResult, submitReserve, clearReserveResult } = useReserveSubmit(params);

  function confirmOption(targetVisibilityDays: number) {
    setOptionDialogOpen(false);
    // OPTION never reads `ttlExpiresAt`; tomorrow only satisfies the required wire field without
    // implying a deadline the caller never set.
    submitReserve('OPTION', targetVisibilityDays, addCalendarDays(new Date(), 1));
  }

  // The deadline preview covers genuinely free cells only: a held-by-other cell is queued, not
  // held now, so a previewed term for it would apply to nothing.
  function openReserveDialog() {
    if (!canSubmitReserve || effectiveOfferId === null) return;
    setReserveDialogOpen(true);
    clearReserveResult();
    previewTtl.mutate({
      offerId: effectiveOfferId,
      status: 'SOFT_RESERVATION',
      cells: toBatchCells(freeCells),
    });
  }

  function confirmReserve(ttlExpiresAt: Date, dateRangeOverride?: DateRange) {
    setReserveDialogOpen(false);
    submitReserve('SOFT_RESERVATION', undefined, ttlExpiresAt, dateRangeOverride);
  }

  return {
    previewTtl,
    optionDialogOpen,
    reserveDialogOpen,
    reserveResult,
    openOptionDialog: () => canSubmitReserve && setOptionDialogOpen(true),
    openReserveDialog,
    confirmOption,
    confirmReserve,
    closeOptionDialog: () => setOptionDialogOpen(false),
    closeReserveDialog: () => setReserveDialogOpen(false),
    dismissReserveResult: clearReserveResult,
  };
}
